import React, { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { api, ApiError } from "../../lib/api"
import { formatClock, formatDate, formatDateTime, formatScore } from "../../lib/format"
import { useStartSimulado } from "../../hooks/useStartSimulado"
import { AppShell } from "../ui/AppShell"
import { Button } from "../ui/Button"
import { Card } from "../ui/Card"
import { PageLoading, Spinner } from "../ui/Spinner"
import { ScoreTrend } from "../ui/ScoreTrend"
import { SubjectBars } from "../ui/SubjectBars"
import { WhyQuestions } from "../shared/WhyQuestions"
import type { ProvaDetailResponse, SimuladoMode, SubjectPerformance } from "../../types"

type InsightTone = "warn" | "up" | "down" | "info"
const TONE_DOT: Record<InsightTone, string> = {
  warn: "bg-red-500",
  up: "bg-emerald-500",
  down: "bg-red-500",
  info: "bg-gold-500",
}

const MODE_INFO: Record<SimuladoMode, { label: string; blurb: string }> = {
  curta: { label: "Curta", blurb: "treino rápido do dia" },
  media: { label: "Média", blurb: "sessão de estudo" },
  oficial: { label: "Oficial", blurb: "simula a prova real, com cronômetro" },
}

const fmtDuration = (min: number) => (min >= 60 ? `${Math.round(min / 60)}h` : `${min} min`)

// Divisas da marca (chevron triplo), decorativas.
function Chevrons({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="square" aria-hidden="true">
      <path d="M4 8.5 12 3.5 20 8.5" />
      <path d="M4 14 12 9 20 14" />
      <path d="M4 19.5 12 14.5 20 19.5" />
    </svg>
  )
}

function weakestOf(list: SubjectPerformance[]): SubjectPerformance | null {
  const answered = list.filter((s) => s.total > 0)
  return answered.length ? answered.reduce((a, b) => (b.accuracy < a.accuracy ? b : a)) : null
}

function strongestOf(list: SubjectPerformance[]): SubjectPerformance | null {
  const answered = list.filter((s) => s.total > 0)
  return answered.length ? answered.reduce((a, b) => (b.accuracy > a.accuracy ? b : a)) : null
}

// Resumo da última nota no lado direito do hero (tema escuro), no mesmo espírito
// do painel de nota da tela de resultado. Anel dourado + variação vs. anterior.
function HeroPerformance({ lastScore, delta, lastDate }: { lastScore: number; delta: number | null; lastDate: string | null }) {
  const r = 46
  const c = 2 * Math.PI * r
  const filled = Math.min(Math.max(lastScore / 10, 0), 1)
  return (
    <div className="flex shrink-0 flex-col items-center gap-2 md:w-52 md:border-l md:border-white/10 md:pl-6">
      <p className="text-[10px] font-bold uppercase tracking-widest text-gold-400">Última nota</p>
      <div className="relative h-28 w-28" role="img" aria-label={`Última nota ${formatScore(lastScore)} de 10`}>
        <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
          <circle cx="60" cy="60" r={r} fill="none" stroke="currentColor" strokeWidth="9" className="text-white/10" />
          <circle
            cx="60"
            cy="60"
            r={r}
            fill="none"
            stroke="currentColor"
            strokeWidth="9"
            strokeLinecap="round"
            strokeDasharray={c}
            strokeDashoffset={c * (1 - filled)}
            className="text-gold-400"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <p className="text-4xl font-extrabold text-white tabular-nums">{formatScore(lastScore)}</p>
          <p className="text-[11px] font-medium text-navy-300">de 10</p>
        </div>
      </div>
      {delta == null ? (
        <p className="text-xs text-navy-300">Primeiro simulado desta prova</p>
      ) : (
        <>
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold tabular-nums ${
              delta > 0
                ? "bg-emerald-500/15 text-emerald-300"
                : delta < 0
                  ? "bg-red-500/15 text-red-300"
                  : "bg-white/10 text-navy-200"
            }`}
          >
            {delta > 0 ? `↑ +${formatScore(delta)}` : delta < 0 ? `↓ ${formatScore(Math.abs(delta))}` : "= estável"}
          </span>
          <span className="text-[11px] text-navy-400">vs. simulado anterior</span>
        </>
      )}
      {lastDate && <p className="text-[11px] text-navy-300">Feito em {formatDate(lastDate)}</p>}
    </div>
  )
}

export default function ProvaDetail() {
  const { id } = useParams<{ id: string }>()
  const [data, setData] = useState<ProvaDetailResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const { start, startingId } = useStartSimulado()

  useEffect(() => {
    api
      .get<ProvaDetailResponse>(`/api/provas/${id}`)
      .then(setData)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "Erro ao carregar a prova"))
  }, [id])

  if (error) {
    return (
      <AppShell>
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center">
          <p className="text-navy-700">{error}</p>
          <Link to="/dashboard">
            <Button variant="secondary">Voltar</Button>
          </Link>
        </div>
      </AppShell>
    )
  }

  if (!data) {
    return (
      <AppShell>
        <PageLoading />
      </AppShell>
    )
  }

  const { prova, performance } = data
  const hasActive = prova.active_attempt_id !== null
  const starting = startingId === prova.id
  const hasPerf = performance.total > 0
  const startsDirect = hasActive || prova.first_simulado
  // O botão vende o resultado (diagnóstico); o helper logo abaixo é que nomeia o
  // mecanismo (nivelamento). Trocar de palavra aqui era a última quebra da
  // continuidade que vem desde o clique na home.
  const ctaLabel = hasActive ? "Continuar simulado" : prova.first_simulado ? "Fazer meu diagnóstico" : "Iniciar simulado"
  const ctaHelper =
    prova.first_simulado && !hasActive
      ? "O nivelamento: 10 questões, sem cronômetro, pra descobrir onde você perde ponto."
      : `Treine no formato da banca até ficar pronto. ${prova.criterio_de_pronto}`

  const ctaClass =
    "inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gold-400 px-8 py-4 font-display text-lg font-bold uppercase tracking-wider text-navy-950 shadow-[0_0_40px_rgba(212,166,42,0.35)] transition hover:bg-gold-300 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"

  const maxWeight = Math.max(1, ...prova.subjects.map((s) => s.weight))

  // Insights (só quando há desempenho)
  const scores = performance.recent.map((r) => r.score)
  const delta = scores.length >= 2 ? Number((scores[scores.length - 1] - scores[scores.length - 2]).toFixed(1)) : null
  const lastDate = performance.recent.length ? performance.recent[performance.recent.length - 1].finished_at : null
  const weakest = weakestOf(performance.by_subject)
  const strongest = strongestOf(performance.by_subject)

  const insights: { tone: InsightTone; text: string }[] = []
  if (weakest && weakest.accuracy < 70) {
    insights.push({ tone: "warn", text: `Ponto fraco: ${weakest.subject} (${weakest.accuracy}%). Priorize os estudos aqui.` })
  }
  if (delta != null && delta > 0) {
    insights.push({ tone: "up", text: `Você subiu ${formatScore(delta)} na última — continue assim!` })
  } else if (delta != null && delta < 0) {
    insights.push({ tone: "down", text: `Caiu ${formatScore(Math.abs(delta))} na última. Bora recuperar.` })
  }
  if (insights.length < 2 && strongest && strongest.accuracy >= 70) {
    insights.push({ tone: "up", text: `Mandou bem em ${strongest.subject} (${strongest.accuracy}%).` })
  }
  if (insights.length === 0) {
    insights.push({ tone: "info", text: "Faça mais simulados desta prova pra acompanhar sua evolução." })
  }

  const tiles = [
    { label: "Simulados", value: String(performance.total) },
    { label: "Média", value: performance.average_score != null ? formatScore(performance.average_score) : "—" },
    { label: "Melhor", value: performance.best_score != null ? formatScore(performance.best_score) : "—" },
    { label: "Tempo médio", value: performance.average_seconds != null ? formatClock(performance.average_seconds) : "—" },
  ]

  const history = [...performance.recent].reverse()

  return (
    <AppShell>
      <div className="flex flex-col gap-6">
        {/* ============ HERO ============ */}
        <section className="relative overflow-hidden rounded-3xl bg-navy-950 px-6 py-8 shadow-xl shadow-navy-900/25 sm:px-9 sm:py-10">
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_85%_60%_at_50%_-5%,rgba(212,166,42,0.16),transparent_65%)]"
            aria-hidden="true"
          />
          <div
            className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(-45deg,rgba(212,166,42,0.04)_0px,rgba(212,166,42,0.04)_1px,transparent_1px,transparent_14px)]"
            aria-hidden="true"
          />
          <Chevrons className="pointer-events-none absolute -right-10 -top-10 h-52 w-52 text-white/[0.04]" />

          <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0 flex-1">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-1 text-sm font-medium text-navy-300 transition hover:text-gold-300"
            >
              ← Provas
            </Link>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className="inline-block rounded-md bg-gold-400/15 px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest text-gold-400">
                {prova.school}
              </span>
              {prova.days_until_exam != null && (
                <span className="inline-flex items-center gap-1.5 text-xs text-navy-300">
                  <Chevrons className="h-3.5 w-3.5 text-gold-500" />
                  faltam <span className="font-display font-bold text-gold-400 tabular-nums">{prova.days_until_exam}</span> dias
                  {prova.exam_date && <span className="text-navy-400"> · {formatDate(prova.exam_date)}</span>}
                </span>
              )}
            </div>

            <h1 className="mt-3 font-display text-3xl font-bold uppercase leading-[0.95] tracking-tight text-white sm:text-5xl">
              {prova.name}
            </h1>

            {/* CTA */}
            <div className="mt-7">
              {startsDirect ? (
                <button type="button" onClick={() => void start(prova)} disabled={starting} className={ctaClass}>
                  {starting && <Spinner className="h-5 w-5 text-navy-950" />}
                  {ctaLabel}
                  {!starting && (
                    <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.21 4.23a.75.75 0 011.06.02l5 5.25a.75.75 0 010 1.04l-5 5.25a.75.75 0 11-1.08-1.04L11.69 10 7.23 5.29a.75.75 0 01-.02-1.06z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ) : (
                <Link to={`/prova/${prova.id}/configurar`} className={ctaClass}>
                  {ctaLabel}
                  <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.21 4.23a.75.75 0 011.06.02l5 5.25a.75.75 0 010 1.04l-5 5.25a.75.75 0 11-1.08-1.04L11.69 10 7.23 5.29a.75.75 0 01-.02-1.06z" clipRule="evenodd" />
                  </svg>
                </Link>
              )}
              <p className="mt-3 text-xs text-navy-300">{ctaHelper}</p>
            </div>
            </div>

            {hasPerf && (
              <HeroPerformance lastScore={performance.last_score ?? 0} delta={delta} lastDate={lastDate} />
            )}
          </div>
        </section>

        {hasPerf ? (
          /* ============ DESEMPENHO ============ */
          <>
            <h2 className="-mb-2 text-sm font-semibold uppercase tracking-wider text-navy-500">Seu desempenho nesta prova</h2>

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {tiles.map((t) => (
                <Card key={t.label} className="p-3! text-center">
                  <p className="text-2xl font-extrabold text-gold-600 tabular-nums">{t.value}</p>
                  <p className="mt-0.5 text-[11px] font-medium uppercase tracking-wide text-navy-400">{t.label}</p>
                </Card>
              ))}
            </div>

            <Card highlighted className="flex flex-col gap-2.5">
              <p className="text-xs font-semibold uppercase tracking-wider text-navy-500">O que os números dizem</p>
              {insights.slice(0, 3).map((ins, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${TONE_DOT[ins.tone]}`} />
                  <p className="text-sm text-navy-800">{ins.text}</p>
                </div>
              ))}
            </Card>

            {performance.recent.length >= 2 && (
              <Card>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-navy-500">Evolução da nota</p>
                <p className="mb-2 text-[11px] text-navy-400">Seus últimos {performance.recent.length} simulados</p>
                <ScoreTrend points={performance.recent} />
              </Card>
            )}

            <Card>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-navy-500">Aproveitamento por matéria</p>
              <SubjectBars subjects={performance.by_subject} />
            </Card>
          </>
        ) : (
          /* ============ INTRO (sem desempenho ainda) ============ */
          <>
            {prova.subjects.length > 0 && (
              <section aria-label="O que cai nesta prova">
                <h2 className="mb-2.5 text-sm font-semibold uppercase tracking-wider text-navy-500">O que cai nesta prova</h2>
                <Card>
                  <p className="mb-4 text-sm text-navy-600">
                    A prova oficial tem <span className="font-semibold text-navy-900">{prova.n_questions} questões</span>{" "}
                    distribuídas assim. Nossos simulados seguem essa mesma proporção.
                  </p>
                  <div className="flex flex-col gap-3">
                    {prova.subjects.map((s) => (
                      <div key={s.slug}>
                        <div className="mb-1 flex items-center justify-between gap-2 text-sm">
                          <span className="text-navy-800">{s.name}</span>
                          <span className="shrink-0 tabular-nums text-navy-500">
                            <span className="font-semibold text-navy-900">{s.weight}</span> questões
                          </span>
                        </div>
                        <div className="h-2.5 overflow-hidden rounded-full bg-slate-200">
                          <div
                            className="h-full rounded-full bg-gold-400"
                            style={{ width: `${Math.round((100 * s.weight) / maxWeight)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </section>
            )}

            <WhyQuestions />

            {prova.modes.length > 0 && (
              <section aria-label="Formatos disponíveis">
                <h2 className="mb-2.5 text-sm font-semibold uppercase tracking-wider text-navy-500">Formatos de simulado</h2>
                <div className="grid gap-3 sm:grid-cols-3">
                  {prova.modes.map((m) => {
                    const info = MODE_INFO[m.mode]
                    return (
                      <Card key={m.mode} className="flex flex-col p-4!">
                        <div className="flex items-baseline justify-between">
                          <span className="font-display text-lg font-bold uppercase tracking-wide text-navy-900">{info.label}</span>
                        </div>
                        <p className="mt-1 text-sm font-medium text-navy-700 tabular-nums">
                          {m.n_questions} questões
                          {m.mode === "oficial" && <span className="text-navy-500"> · {fmtDuration(m.duration_minutes)}</span>}
                        </p>
                        <p className="mt-0.5 text-xs text-navy-400">{info.blurb}</p>
                      </Card>
                    )
                  })}
                </div>
              </section>
            )}

            <Card highlighted className="flex items-center gap-3">
              <Chevrons className="h-7 w-7 shrink-0 text-gold-500" />
              <p className="text-sm text-navy-800">
                Faça o nivelamento pra descobrir por onde começar — depois você acompanha nota, evolução e aproveitamento por
                matéria bem aqui.
              </p>
            </Card>
          </>
        )}

        {/* ============ HISTÓRICO ============ */}
        {history.length > 0 && (
          <section aria-label="Simulados desta prova">
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-navy-500">Simulados desta prova</h2>
            <div className="flex flex-col gap-2">
              {history.map((r) => (
                <Card key={r.id} className="flex items-center justify-between gap-3 p-4!">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-200 text-base font-bold tabular-nums text-gold-600">
                      {formatScore(r.score)}
                    </div>
                    <p className="text-sm font-medium text-navy-900">{formatDateTime(r.finished_at)}</p>
                  </div>
                  <Link to={`/simulado/${r.id}/resultado`} className="text-sm font-medium text-gold-600 hover:text-gold-700">
                    resultado
                  </Link>
                </Card>
              ))}
            </div>
          </section>
        )}
      </div>
    </AppShell>
  )
}
